import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useHistory } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
    Checkbox, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, TableFooter
} from '@mui/material';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import { Tooltip } from '@mui/material';
import { useLocation, Link } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';

function RoomIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const history = useHistory(); // Access the navigation history
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');
    const [open, setOpen] = useState(false);
    const [requestedItemdata, setrequestedItemdata] = useState([]);
    const [purchaseRequestdatacount, setpurchaseRequestdatacount] = useState('');
    const location = useLocation();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const shouldReload = localStorage.getItem('shouldReload') === 'true';

        // Only reload data if navigated from PurchaseOrderCreate and flag is set
        if (location.state?.from === '/RoomCreate' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else if (location.state?.from === '/RoomEdit' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        // else if (location.state?.from === '/JournalEntryDetails' && shouldReload) {
        //     localStorage.removeItem('shouldReload'); // Clear the flag after use
        //     window.location.reload();
        // }
        else {
            // Populate data without reloading for other cases
            setFromDate(getFormattedStartDate());
            setToDate(getFormattedCurrentDate());
        }
    }, [location]);

    const handleClose = () => {
        setOpen(false);
    };

    // Define the modal style
    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%',
        bgcolor: 'white',
        boxShadow: 24,
        borderRadius: '10px'
    };


    useEffect(() => {
        // Check if reload is needed based on the flag set in localStorage
        const shouldReload = localStorage.getItem('shouldReloadCreatePage') === 'true';

        if (shouldReload) {
            localStorage.removeItem('shouldReloadCreatePage'); // Remove the flag after use
            window.location.reload(); // Reload the page once
        }

        // Authentication and token expiration handling
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchTableData();
        // Other initializations, if any
    }, [isAuthenticated, token, logout]);



    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    //handle search
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    //search
    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };


    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const fetchTableData = async () => {

        setData([]);
        setLoading(true);
        try {
            const response = await fetchData('RoomApi', `/${companyId}/${branchId}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Room Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        } finally {
            setLoading(false);
        }

    };


    const handleDelete = async () => {
        deleteData('RoomApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('RoomApi', `${itemId}`, token)
            .then(() => {
                fetchTableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    const commonButtonStyles = {
        height: '36px',
        marginLeft: '10px',
        marginTop: '10px',
        marginRight: '8px',
    };

    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(8);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const sortedData = stableSort(filteredData, getComparator(order, orderBy));

    return (
        <div className="w-full  mx-auto" style={{ width: '80%', marginTop: '10px' }}>
            <div className='w-full mb-2 flex'>
                <h6 className='block uppercase tracking-wide text-black productHead flex-grow linkcolor' style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}>Rooms</h6>

                <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'left', marginTop: '17px' }}>
                    <input
                        className="shadow text-gray-700 leading-tight"
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={handleSearch}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        style={{
                            width: isFocused ? '270px' : '200px',
                            height: '40px',
                            border: '1px solid #ccc',
                            borderRadius: '15px',
                            padding: '0 20px',
                            transition: 'width 0.4s ease-in-out, border-color 0.3s ease-in-out',
                            outline: 'none',
                            borderColor: isFocused ? 'darkcyan' : '#ccc',
                            boxShadow: isFocused ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
                        }}
                    />
                </div>
                {loading && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}>
                        <div>
                            <CircularProgress style={{ color: 'white' }} />
                            <span style={{ color: 'white', marginTop: '10px', display: 'block' }}>Loading...</span>
                        </div>
                    </div>
                )}
                {
                    hasCreateMasterPermission && (
                        <Tooltip
                            title="Create"
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: '#F3D0D7',
                                        color: 'black',
                                        border: '1px solid white',
                                        fontWeight: 'bold',
                                        boxShadow: 2,
                                    },
                                },
                            }}
                        >
                            <Button
                                style={{ backgroundColor: '#F3D0D7', color: "black", height: '36px', marginLeft: '10px', marginTop: '1.30%' }}
                                component={Link}
                                to="/RoomCreate"
                                className="linkcolor"
                            >
                                <span>Create</span>
                            </Button>
                        </Tooltip>
                    )
                }
            </div >

            <div className="watermark">
                <CopyrightIcon /><b style={{ fontFamily: 'Script Bold Italic' }}>EFFTABS SOLUTIONS LLP</b>
            </div>

            {/* <div className='w-full mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto', marginTop: '-1%' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead sx={{ backgroundColor: '#C1BAA1', width: '2500px' }}>
                            <TableRow sx={{ whiteSpace: 'nowrap' }}>
                                {['Sl.No', 'Name', 'Description'].map((column, index) => (
                                    <TableCell
                                        key={index}
                                        sx={{
                                            borderBottom: '1px solid #F5F5F5',
                                            fontSize: '14px',
                                            padding: '7px',
                                            color: 'darkblue',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        <TableSortLabel
                                            active={orderBy === column}
                                            direction={orderBy === column ? order : 'asc'}
                                            onClick={(e) => handleRequestSort(e, column)}
                                        >
                                            {column}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '14px', padding: '4px', color: 'whitesmoke' }}></TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody style={{ backgroundColor: '#F5F7F8' }}>
                            {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((data, index) => (
                                <TableRow
                                    key={index}
                                    className="border-b"
                                    style={{
                                        backgroundColor: index % 2 === 0 ? '#F7FAFF' : '#FFFFFF',
                                        borderBottom: '1px solid #E0E0E0',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#E1F0DA')} 
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#F7FAFF' : '#FFFFFF')} 
                                >
                                    <TableCell sx={{ borderBottom: '1px solid #D4E2D4', color: 'black', padding: '3px', textAlign: 'left', fontSize: '14px', width: '2%' }}>
                                        {index + 1}
                                    </TableCell>

                                    <TableCell sx={{ borderBottom: '1px solid #D4E2D4', color: 'black', padding: '3px', textAlign: 'left', fontSize: '14px', width: '20%' }}>
                                        {data.Name}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #D4E2D4', color: 'black', padding: '3px', textAlign: 'left', fontSize: '14px', width: '25%' }}>
                                        {data.Description}
                                    </TableCell>



                                    <TableCell sx={{ borderBottom: '1px solid #C0C0C0', height: '20px', padding: '5px', textAlign: 'right', width: '15%' }}>

                                      
                                        {hasEditMasterPermission && (
                                            <IconButton component={Link} to={`/RoomEdit/${data.Id}`} >
                                                <EditIcon style={{ height: '22px', color: 'darkblue' }} />
                                            </IconButton>
                                        )}
                                        {hasDeleteMasterPermission && (
                                            <IconButton onClick={() => (data.IsActive ? openConfirmDialog(data.Id) : handleActivate(data.Id))} color="error">
                                                {data.IsActive ? <DeleteIcon style={{ height: '22px', color: 'darkred' }} /> : <ActivateIcon style={{ color: 'darkolivegreen', height: '22px' }} />}
                                            </IconButton>
                                        )}

                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>

                        <Dialog
                            open={confirmDialogOpen}
                            onClose={closeConfirmDialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    Are you sure you want to delete this record?
                                </DialogContentText>
                            </DialogContent>

                            <DialogActions>
                                <button
                                    onClick={closeConfirmDialog}
                                    style={{
                                        backgroundColor: '#dc3545',
                                        color: '#ffffff',
                                        border: '1px solid #dc3545',
                                        borderRadius: '4px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        marginRight: '8px',
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    style={{
                                        backgroundColor: '#28a745',
                                        color: '#ffffff',
                                        border: '1px solid #28a745',
                                        borderRadius: '4px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    Confirm
                                </button>
                            </DialogActions>

                        </Dialog>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />

                </TableContainer>
            </div>

        </div > */}
            <div className='w-full mb-2 flex' style={{ maxHeight: '520px', overflowY: 'hidden' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead sx={{ backgroundColor: '#C1BAA1' }}>

                            <TableRow>
                                <TableCell sx={{ backgroundColor: '#ECEBDE', borderBottom: '1px solid #F5F5F5', color: 'darkred', fontWeight: 'bolder', padding: '5px' }}>Sl.No</TableCell>
                                <TableCell sx={{ backgroundColor: '#ECEBDE', borderBottom: '1px solid #F5F5F5', color: 'darkred', fontWeight: 'bolder', padding: '5px' }}>Name</TableCell>
                                <TableCell sx={{ backgroundColor: '#ECEBDE', borderBottom: '1px solid #F5F5F5', color: 'darkred', fontWeight: 'bolder', padding: '5px', textAlign: 'right' }}></TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredData.map((item, index) => (
                                <TableRow
                                    key={index}
                                    className="border-b"
                                    style={{
                                        backgroundColor: index % 2 === 0 ? '#F7FAFF' : '#FFFFFF',
                                        borderBottom: '1px solid #E0E0E0',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#E1F0DA')} // Light wave color
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#F7FAFF' : '#FFFFFF')} // Reset color on hover out
                                >
                                    {/* Table cells */}
                                    <TableCell
                                        style={{
                                            fontSize: '13px',
                                            fontWeight: 'bold',
                                            padding: '1px',
                                            textAlign: 'left',
                                            width: '65px',
                                        }}
                                    >
                                        {index + 1}
                                    </TableCell>
                                    <TableCell
                                        style={{
                                            fontSize: '13px',
                                            fontWeight: 'bold',
                                            padding: '3px',

                                        }}
                                    >
                                        {item.Name}
                                    </TableCell>
                                    <TableCell
                                        style={{
                                            fontSize: '13px',
                                            fontWeight: 'bold',
                                            padding: '3px',
                                            textAlign: 'right'
                                        }}
                                    >
                                        {hasEditMasterPermission && (
                                            <IconButton component={Link} to={`/RoomEdit/${item.Id}`} >
                                                <EditIcon style={{ height: '22px', color: 'darkblue' }} />
                                            </IconButton>
                                        )}
                                        {hasDeleteMasterPermission && (
                                            <IconButton onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))} color="error">
                                                {item.IsActive ? <DeleteIcon style={{ height: '22px', color: 'darkred' }} /> : <ActivateIcon style={{ color: 'darkolivegreen', height: '22px' }} />}
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>

                        <Dialog
                            open={confirmDialogOpen}
                            onClose={closeConfirmDialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    Are you sure you want to delete ?
                                </DialogContentText>
                            </DialogContent>

                            <DialogActions>
                                <button
                                    onClick={closeConfirmDialog}
                                    style={{
                                        backgroundColor: '#dc3545',
                                        color: '#ffffff',
                                        border: '1px solid #dc3545',
                                        borderRadius: '4px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        marginRight: '8px',
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    style={{
                                        backgroundColor: '#28a745',
                                        color: '#ffffff',
                                        border: '1px solid #28a745',
                                        borderRadius: '4px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    Confirm
                                </button>
                            </DialogActions>

                        </Dialog>
                    </Table>
                </TableContainer>
            </div>
        </div>

    );
}


function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export default RoomIndex;

